// Servizio per la gestione delle prenotazioni

import apiClient from './apiClient';
import { Booking, ApiResponse } from '../types';

// Interfaccia per i parametri di ricerca
export interface BookingSearchParams {
    patient_id?: string;
    execution_unit_id?: string;
    from_date?: string;
    to_date?: string;
    status_id?: number;
    active_only?: boolean;
    limit?: number;
}

// Interfaccia per le statistiche
export interface BookingStats {
    status_id: number;
    status_name: string;
    count: number;
}

// Interfaccia per la sincronizzazione
export interface SyncOptions {
    force_overwrite?: boolean;
}

export interface SyncResult {
    type: string;
    processed: number;
    created: number;
    updated: number;
    skipped: number;
    errors: number;
    duration: number;
    timestamp: string;
}

// Funzione per recuperare tutte le prenotazioni
export const getBookings = async (params?: BookingSearchParams): Promise<Booking[]> => {
    try {
        const response = await apiClient.get<ApiResponse<Booking[]>>('/bookings', { params });
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero delle prenotazioni:', error);
        return [];
    }
};

// Funzione per recuperare una prenotazione specifica
export const getBooking = async (id: string): Promise<Booking> => {
    try {
        const response = await apiClient.get<ApiResponse<Booking>>(`/bookings/${id}`);
        if (response.data && 'data' in response.data) {
            return response.data.data;
        }
        throw new Error('Prenotazione non trovata');
    } catch (error) {
        console.error('Errore durante il recupero della prenotazione:', error);
        throw error;
    }
};

// Funzione per creare una nuova prenotazione
export const createBooking = async (booking: Omit<Booking, 'id' | 'createdAt' | 'updatedAt'>): Promise<Booking> => {
    try {
        const response = await apiClient.post<ApiResponse<Booking>>('/bookings', booking);
        if (response.data && 'data' in response.data) {
            return response.data.data;
        }
        throw new Error('Errore nella creazione della prenotazione');
    } catch (error) {
        console.error('Errore durante la creazione della prenotazione:', error);
        throw error;
    }
};

// Funzione per aggiornare una prenotazione
export const updateBooking = async (id: string, booking: Partial<Booking>): Promise<Booking> => {
    try {
        const response = await apiClient.put<ApiResponse<Booking>>(`/bookings/${id}`, booking);
        if (response.data && 'data' in response.data) {
            return response.data.data;
        }
        throw new Error('Errore nell\'aggiornamento della prenotazione');
    } catch (error) {
        console.error('Errore durante l\'aggiornamento della prenotazione:', error);
        throw error;
    }
};

// Interfaccia per le opzioni di cancellazione
export interface DeleteOptions {
    type: 'soft' | 'hard';
    reason?: string;
    isHardDelete: boolean;
}

// Risultato della cancellazione
export interface DeleteResult {
    success: boolean;
    message: string;
    details?: {
        local_deleted: boolean;
        recup_deleted: boolean;
        recup_id?: string | null;
    };
    note?: string;
}

// Funzione per eliminare una prenotazione (soft o hard delete)
export const deleteBooking = async (id: string, options: DeleteOptions): Promise<DeleteResult> => {
    try {
        const params = new URLSearchParams();
        if (options.isHardDelete) {
            params.append('hard_delete', 'true');
        }

        const response = await apiClient.delete<DeleteResult>(`/bookings/${id}?${params.toString()}`, {
            data: options.reason ? { reason: options.reason } : undefined
        });
        
        return response.data;
    } catch (error) {
        console.error('Errore durante l\'eliminazione della prenotazione:', error);
        throw error;
    }
};

// Backward compatibility: funzione legacy per soft delete
export const deleteBookingLegacy = async (id: string, reason?: string): Promise<void> => {
    await deleteBooking(id, {
        type: 'soft',
        reason,
        isHardDelete: false
    });
};

// Funzione per ripristinare una prenotazione eliminata
export const restoreBooking = async (id: string): Promise<void> => {
    try {
        await apiClient.post(`/bookings/${id}/restore`);
    } catch (error) {
        console.error('Errore durante il ripristino della prenotazione:', error);
        throw error;
    }
};

// Funzione per ottenere prenotazioni eliminate
export const getDeletedBookings = async (): Promise<Booking[]> => {
    try {
        const response = await apiClient.get<ApiResponse<Booking[]>>('/bookings/deleted/list');
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero delle prenotazioni eliminate:', error);
        return [];
    }
};

// Funzione per ottenere audit trail di una prenotazione
export const getBookingAuditTrail = async (id: string): Promise<any[]> => {
    try {
        const response = await apiClient.get<ApiResponse<any[]>>(`/bookings/${id}/audit`);
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero dell\'audit trail:', error);
        return [];
    }
};

// Funzione per ottenere statistiche per stato
export const getBookingStatsByStatus = async (): Promise<BookingStats[]> => {
    try {
        const response = await apiClient.get<ApiResponse<BookingStats[]>>('/bookings/stats/by-status');
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero delle statistiche:', error);
        return [];
    }
};

// Funzione per ottenere prenotazioni recenti
export const getRecentBookings = async (limit: number = 10): Promise<Booking[]> => {
    try {
        const response = await apiClient.get<ApiResponse<Booking[]>>('/bookings/recent', {
            params: { limit }
        });
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante il recupero delle prenotazioni recenti:', error);
        return [];
    }
};

// Funzione per sincronizzare da RECUP
export const syncBookingsFromRecup = async (options: SyncOptions = {}): Promise<SyncResult> => {
    try {
        // Usa syncOperation con timeout di 10 minuti per le operazioni di sync
        const response = await apiClient.syncOperation<SyncResult>('/bookings/sync/recup', options, 600000);
        if (response.data && 'data' in response.data) {
            return response.data.data;
        }
        throw new Error('Errore nella sincronizzazione');
    } catch (error) {
        // Gestisce il caso di conferma richiesta
        if (error.response?.status === 409) {
            throw {
                ...error,
                confirmationRequired: true,
                existingCount: error.response.data.existing_count
            };
        }
        console.error('Errore durante la sincronizzazione da RECUP:', error);
        throw error;
    }
};

// Funzione per ottenere statistiche di sincronizzazione
export const getSyncStats = async (): Promise<any> => {
    try {
        const response = await apiClient.get<ApiResponse<any>>('/bookings/sync/stats');
        return response.data.data;
    } catch (error) {
        console.error('Errore durante il recupero delle statistiche di sincronizzazione:', error);
        throw error;
    }
};

// Funzione per verificare lo stato di salute della sincronizzazione
export const getSyncHealth = async (): Promise<any> => {
    try {
        const response = await apiClient.get<ApiResponse<any>>('/bookings/sync/health');
        return response.data.data;
    } catch (error) {
        console.error('Errore durante il controllo dello stato di salute:', error);
        throw error;
    }
};

// Funzione per ottenere le agende disponibili per la sincronizzazione
export const getAvailableDiaries = async (): Promise<any> => {
    try {
        const response = await apiClient.get<ApiResponse<any>>('/bookings/sync/diaries');
        return response.data;
    } catch (error) {
        console.error('Errore durante il recupero delle agende:', error);
        throw error;
    }
};

// Funzione per cercare pazienti
export const searchPatient = async (query: string): Promise<any[]> => {
    try {
        const response = await apiClient.get(`/patients/search`, { params: { q: query } });
        if (response.data && 'data' in response.data) {
            return response.data.data || [];
        }
        return [];
    } catch (error) {
        console.error('Errore durante la ricerca pazienti:', error);
        return [];
    }
};

// Funzione per sincronizzare da RECUP (alias)
export const syncFromRecup = async (options: SyncOptions = {}): Promise<SyncResult> => {
    return syncBookingsFromRecup(options);
};

// Oggetto servizio di default per compatibilità
const bookingService = {
    getBookings,
    getBooking,
    createBooking,
    updateBooking,
    deleteBooking,
    deleteBookingLegacy,
    getBookingStatsByStatus,
    getRecentBookings,
    syncBookingsFromRecup,
    syncFromRecup,
    getAvailableDiaries,
    searchPatient,
    getSyncStats,
    getSyncHealth
};

export { bookingService };
export default bookingService;
